
import { Link } from 'react-router-dom';

const benefits = [
  {
    icon: 'ri-eye-line',
    title: 'Maximum Visibility',
    description:
      'Get discovered by thousands of Roseville residents and visitors actively searching for local businesses every month.',
    stat: '50K+',
    statLabel: 'Monthly Visitors',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: '100% Free Listing',
    description:
      'Create your business profile at no cost. No hidden fees, no credit card required — just sign up and start getting noticed.',
    stat: '$0',
    statLabel: 'To Get Started',
  },
  {
    icon: 'ri-star-smile-line',
    title: 'Build Trust with Reviews',
    description:
      'Collect and showcase authentic customer reviews. Respond directly to feedback and build a stellar reputation in the community.',
    stat: '12K+',
    statLabel: 'Reviews Posted',
  },
  {
    icon: 'ri-map-pin-user-line',
    title: 'Local SEO Boost',
    description:
      'Your listing is optimized for local search, helping customers in Roseville and Placer County find you on Google and other search engines.',
    stat: '#1',
    statLabel: 'Local Directory',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Owner Dashboard & Analytics',
    description:
      'Track views, calls, and engagement from your dedicated dashboard. Understand your customers and grow smarter.',
    stat: '24/7',
    statLabel: 'Access & Insights',
  },
  {
    icon: 'ri-megaphone-line',
    title: 'Promotion Opportunities',
    description:
      'Boost your reach with featured placements, deals & coupons, and spotlight features to stand out from the competition.',
    stat: '3x',
    statLabel: 'More Engagement',
  },
];

export default function WhyListWithUs() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full mb-5">
            <i className="ri-rocket-2-line text-teal-600" />
            <span className="text-teal-700 text-sm font-semibold tracking-wide uppercase">
              Grow Your Business
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Why List with <span className="text-teal-600">Roseville.org</span>?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Join hundreds of thriving local businesses already connecting with customers through Roseville&apos;s most
            trusted community platform.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-2xl p-7 border border-gray-100 shadow-sm hover:shadow-lg hover:border-teal-200 transition-all duration-500"
            >
              <div className="flex items-start justify-between mb-5">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-xl group-hover:bg-teal-100 transition-colors duration-300">
                  <i className={`${benefit.icon} text-2xl text-teal-600`} />
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black text-teal-600 leading-none">{benefit.stat}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{benefit.statLabel}</div>
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div className="bg-gradient-to-br from-stone-50 to-gray-50 rounded-2xl p-10 md:p-14 mb-16 border border-gray-100">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-12">
            Get Listed in <span className="text-teal-600">3 Simple Steps</span>
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                icon: 'ri-user-add-line',
                title: 'Create Your Profile',
                description:
                  'Fill out your business details — name, address, hours, photos, and a description of what makes you special.',
              },
              {
                step: '02',
                icon: 'ri-check-double-line',
                title: 'Get Verified',
                description: 'Our team reviews your listing to ensure accuracy and quality. Most listings go live within 24 hours.',
              },
              {
                step: '03',
                icon: 'ri-line-chart-line',
                title: 'Start Growing',
                description:
                  'Appear in search results, collect reviews, respond to customers, and watch your business grow.',
              },
            ].map((item, index) => (
              <div key={index} className="relative text-center">
                <div className="text-6xl font-black text-teal-100 mb-3 leading-none">{item.step}</div>
                <div className="w-14 h-14 flex items-center justify-center bg-white rounded-2xl shadow-sm border border-gray-100 mx-auto mb-4">
                  <i className={`${item.icon} text-2xl text-teal-600`} />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">
                  <a href="/submit-business" className="hover:text-teal-600 transition-colors">
                    {item.title}
                  </a>
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                {index < 2 && (
                  <div className="hidden md:block absolute top-10 -right-4 w-8 h-8 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xl text-teal-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="inline-flex flex-col items-center">
            <p className="text-gray-500 text-sm mb-5">
              Trusted by <strong className="text-gray-900">200+ local businesses</strong> across Roseville
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/submit-business"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-teal-600 text-white rounded-lg text-base font-semibold hover:bg-teal-700 transition-colors shadow-md shadow-teal-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-store-2-line text-lg" />
                List Your Business for Free
              </Link>
              <Link
                to="/advertise"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-gray-700 rounded-lg text-base font-semibold hover:bg-gray-50 transition-colors border border-gray-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-vip-crown-line text-lg" />
                Explore Premium Plans
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
